
// Vuex/Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Mutation, State } from "vuex-class";

// Sv Components
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

// Types
import { ContactRole, UserDetails, ContactUser } from "@/store/user-management/types";
import { User } from "@/store/types";

// Vuelidate
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength, email } from "vuelidate/lib/validators";

// Third Party
import vselect3 from "vselect3";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { isEqual, cloneDeep, isEmpty } from "lodash";

// Namespaces
const Areas = namespace("areas");
const UserManagement = namespace("userManagement");

@Component({
	mixins: [validationMixin],
	components: {
		"area-tree-select": AreaTreeSelect,
		vselect3,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	},
	validations: {
		currentContactRole: {
			name: {
				required,
				minLength: minLength(3),
				maxLength: maxLength(50)
			},
			email: {
				minLength: minLength(3),
				maxLength: maxLength(150),
				email
			},
			groupID: {
				required,
				maxValue: value => value > 0
			},
			telephone: {
				minLength: minLength(3),
				maxLength: maxLength(50)
			},
			mobile: {
				minLength: minLength(3),
				maxLength: maxLength(50)
			},
			notes: {
				maxLength: maxLength(500)
			},
			address: {
				maxLength: maxLength(200)
			}
		},
		hasDataChanged: {
			required: requiredIf(function() {
				return isEqual(this.contactRole, this.currentContactRole);
			})
		},
		hasContactRoleInfoBeenPopulatedCorrectly: {
			required: requiredIf(function() {
				if (this.currentContactRole.contactRoles) {
					return (
						!this.currentContactRole.contactRoleGroups || !this.currentContactRole.contactRoleGroups.length
					);
				}

				if (this.currentContactRole.contactRoleGroups && this.currentContactRole.contactRoleGroups.length > 0) {
					return !this.currentContactRole.contactRoles;
				}

				return false;
			})
		}
	}
})
export default class ContactsModal extends Vue {
	// -- Vuex -- //
	@Mutation private setIsLoading: (loading: boolean) => void;
	@State(state => state.User) user: User;



	@UserManagement.State contactRoles: ContactRole[];
	@UserManagement.Action getUserById: (userId: number) => Promise<UserDetails>;
	@UserManagement.Action createUser: (newUser: UserDetails) => Promise<number>;
	@UserManagement.Action removeContact: (userId: number) => Promise<void>;
	@UserManagement.Action getContactRoleGroups: (userId: number) => Promise<void>;
	@UserManagement.Action updateUser: (userDetails: UserDetails) => Promise<void>;
	@UserManagement.Action updateRole: ({
		updatedUser,
		roleIds,
		groups
	}: {
		updatedUser: UserDetails;
		roleIds: number[];
		groups: number[];
	}) => Promise<void>;

	// -- Props -- //

	@Prop({ default: false, type: Boolean }) modalVisible: boolean;
	@Prop({ default: null, type: Object }) contactRole: ContactUser;
	@Prop({ default: true, type: Boolean }) readonly: boolean;

	// -- Properties -- //

	private readonly currentContactRoleTemplate: ContactUser = {
		userID: 0,
		username: "",
		email: "",
		name: "",
		address: "",
		telephone: "",
		mobile: "",
		groupID: 0,
		isSiteStaff: false,
		permissions: [],
		contactRoleGroups: [],
		contactRoles: null,
		role: "",
		sitePassword: "",
		notes: "",
		receiveIncidentEmails: false,
		receiveUpdateEmails: false
	};

	private currentContactRole: ContactUser = { ...this.currentContactRoleTemplate };

	private isNew: boolean = true;

	private areaSelectLoaded: boolean = false;
	private roleAreaSelectLoaded: boolean = false;
	private modalStateSet: boolean = false;

	// -- Watchers -- //

	@Watch("modalVisible")
	private async setState(newModalState: boolean): Promise<void> {
		if (!newModalState) {
			this.modalStateSet = false;
			this.areaSelectLoaded = false;
			this.roleAreaSelectLoaded = false;
			return;
		}

		await this.setContact();
		this.modalStateSet = true;
	}

	@Watch("contactsModalLoaded")
	private onContactsModalLoaded(newValue: boolean, oldValue: boolean) {
		if (newValue) {
			this.setIsLoading(false);
		}
	}

	// -- Computed Properties -- //
	private get currentContactRoleGroups(): number[] {
		if (!this.currentContactRole || !this.currentContactRole.contactRoleGroups) return [];
		return this.currentContactRole.contactRoleGroups;
	}

	private set currentContactRoleGroups(groupIds: number[]) {
		this.currentContactRole.contactRoleGroups = groupIds
	}


	private get contactsModalLoaded(): boolean {
		return this.modalStateSet && this.areaSelectLoaded && this.roleAreaSelectLoaded;
	}

	// -- Methods -- //

	private emitContactRole(): void {
		if (!this.currentContactRole) return;
		this.$emit("contact-role", this.currentContactRole);
	}

	private async setContact(): Promise<void> {
		if (!this.contactRole || isEmpty(this.contactRole)) {
			this.currentContactRole = { ...this.currentContactRoleTemplate };

			this.isNew = true;
			return;
		}

		this.isNew = false;
		this.currentContactRole = cloneDeep(this.contactRole);
		await this.getContactRoleGroups(this.user.userId);	
	}

	private async deleteCurrentUser(): Promise<void> {
		await this.removeContact(this.currentContactRole.userID);
		this.closeModal();
	}

	public async updateUserDetails(): Promise<void> {
		let oldUser: UserDetails = null;

		if (this.currentContactRole.userID > 0) {
			// We need to get the current user to fill in the blanks.
			oldUser = await this.getUserById(this.currentContactRole.userID);
		}

		const user: UserDetails = {
			userID: this.currentContactRole.userID,
			username: this.currentContactRole.username,
			fullName: this.currentContactRole.name,
			email: this.currentContactRole.email,
			address: this.currentContactRole.address,
			telephone: this.currentContactRole.telephone,
			mobile: this.currentContactRole.mobile,
			groupID: this.currentContactRole.groupID,
			isSiteStaff: this.currentContactRole.isSiteStaff,
			mobileAppEnabled: oldUser ? oldUser.mobileAppEnabled : false,
			groupName: oldUser ? oldUser.groupName : "",
			disabledAt: oldUser ? oldUser.disabledAt : null,
			lockedOutUntil: oldUser ? oldUser.lockedOutUntil : null,
			lastLoginAt: oldUser ? oldUser.lastLoginAt : null,
			password: oldUser ? oldUser.password : "",
			permissions: oldUser ? oldUser.permissions : [],
			permissionsGroup: oldUser ? oldUser.permissionsGroup : [],
			userGroupMembershipTitles: oldUser ? oldUser.userGroupMembershipTitles : [],
			timeZoneId: oldUser ? oldUser.timeZoneId : 0,
			passwordSalt: oldUser ? oldUser.passwordSalt : "",
			name: this.currentContactRole.name,
			notes: this.currentContactRole.notes,
			receiveIncidentEmails: this.currentContactRole.receiveIncidentEmails,
			receiveUpdateEmails: this.currentContactRole.receiveUpdateEmails,
			sitePassword: this.currentContactRole.sitePassword
		};

		this.currentContactRole.userID > 0 ? await this.updateUser(user) : (user.userID = await this.createUser(user));

		// Compare our roles and groups to see if we need to update our roles.
		if (
			(this.isNew &&
				(this.currentContactRole.contactRoleGroups.length > 0 ||
					this.currentContactRole.contactRoles)) ||
			(!this.isNew &&
				(!isEqual(
					this.contactRole.contactRoleGroups,
					this.currentContactRoleGroups
				) ||
					!this.currentContactRole.contactRoles ||
					!this.contactRole.contactRoles ||
					this.currentContactRole.contactRoles.contactRoleId !== this.contactRole.contactRoles.contactRoleId))
		) {
			await this.updateRole({
				updatedUser: user,
				roleIds: this.currentContactRole.contactRoles
					? [this.currentContactRole.contactRoles.contactRoleId]
					: [],
				groups: this.currentContactRole.contactRoleGroups
			});
		}

		this.closeModal();
	}

	@Emit("toggle-modal")
	@Emit("contact-role")
	private closeModal(): void {
		this.currentContactRole = null;
	}
}
